import { Box, Divider, TextField, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMemo, useState } from "react";

import { Checkbox, FormControlLabel, FormGroup } from "../";

interface GroupedCheckboxOption<T extends string> {
  label: string;
  value: T;
  section: string;
  tooltip?: string;
}

interface GroupedCheckboxesProps<T extends string> {
  options: GroupedCheckboxOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  isSearchable?: boolean;
  disableSort?: boolean;
  maxHeight?: number;
}

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.875rem",
  color: theme.palette.secondary[3],
  textTransform: "capitalize",
  letterSpacing: "0.05em",
  padding: theme.spacing(0.5, 0),
  pointerEvents: "none",
}));

const SectionGroup = styled(Box)(({ theme }) => ({
  "&:not(:first-of-type)": {
    marginTop: theme.spacing(2),
  },
}));

export const GroupedCheckboxes = <T extends string>(
  props: GroupedCheckboxesProps<T>,
) => {
  const {
    options,
    value,
    onChange,
    isSearchable = false,
    disableSort = false,
    maxHeight = 300,
  } = props;
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const filtered =
      isSearchable && searchQuery
        ? options.filter((option) =>
            option.label.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : options;

    // Sort within sections if not disabled
    const sorted = disableSort
      ? filtered
      : filtered.slice().sort((a, b) => {
          // First sort by section
          if (a.section !== b.section) {
            return a.section.localeCompare(b.section);
          }
          // Then sort alphabetically within section
          return a.label.localeCompare(b.label, undefined, {
            sensitivity: "base",
          });
        });

    // For isSearchable, put checked first within each section while preserving section order
    if (isSearchable) {
      // Group by section preserving order
      const sectionOrder: string[] = [];
      const bySection: Record<string, typeof sorted> = {};
      for (const option of sorted) {
        if (!bySection[option.section]) {
          bySection[option.section] = [];
          sectionOrder.push(option.section);
        }
        bySection[option.section].push(option);
      }

      // For each section, put checked items first
      const result: typeof sorted = [];
      for (const section of sectionOrder) {
        const sectionItems = bySection[section];
        const checked = sectionItems.filter((opt) => value.includes(opt.value));
        const unchecked = sectionItems.filter(
          (opt) => !value.includes(opt.value),
        );
        result.push(...checked, ...unchecked);
      }
      return result;
    }

    return sorted;
  }, [options, searchQuery, isSearchable, disableSort, value]);

  const groupedOptions = useMemo(
    () =>
      filteredOptions.reduce<
        { section: string; items: typeof filteredOptions }[]
      >((groups, option) => {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup?.section === option.section) {
          lastGroup.items.push(option);
        } else if (option.section) {
          groups.push({ section: option.section, items: [option] });
        }
        return groups;
      }, []),
    [filteredOptions],
  );

  const handleChange =
    (optionValue: T) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      let newValue: T[];
      if (checked) {
        newValue = [...value, optionValue];
      } else {
        newValue = value.filter((v) => v !== optionValue);
      }

      onChange(newValue);
    };

  return (
    <>
      {isSearchable && (
        <>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <TextField
              placeholder="Search..."
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          maxHeight,
          overflow: "auto",
          px: 3,
          py: 1,
        }}
      >
        <FormGroup>
          {groupedOptions.map((group) => (
            <SectionGroup key={group.section}>
              <SectionHeader>{group.section}</SectionHeader>
              {group.items.map((option) => {
                const checkboxControl = (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={value.includes(option.value)}
                        onChange={handleChange(option.value)}
                        size="small"
                      />
                    }
                    sx={{
                      display: "flex",
                    }}
                    label={<Typography noWrap>{option.label}</Typography>}
                  />
                );

                return option.tooltip ? (
                  <Tooltip
                    key={option.value}
                    title={option.tooltip}
                    placement="right"
                    enterDelay={300}
                    arrow
                  >
                    <Box>{checkboxControl}</Box>
                  </Tooltip>
                ) : (
                  checkboxControl
                );
              })}
            </SectionGroup>
          ))}
          {filteredOptions.length === 0 && (
            <Typography color="text.secondary">No results</Typography>
          )}
        </FormGroup>
      </Box>
    </>
  );
};
